import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { Popup } from '_atoms';
import type { SelectOption } from '_atoms/Select';
import { BreadCrumbs } from '_atoms/BreadCrumbs';
import { FormCreateEdit, PopupReassignRecruiter, JobCreationForm } from '_molecules';
import history from 'helpers/history';
import { MixpanelService } from 'helpers/Mixpanel';
import { ROLES } from 'permission';
import { getHrManagers, getRecruiterLeads, getServices } from 'store/selectors/commonSelectors';
import { INITIAL_BENEFITS } from '_molecules/JobCreationForm/constants';
import { JOB_STATUSES } from 'constants/job';
import { BUTTON_TEXT, POPUP_BODY, POPUP_LABELS } from 'constants/text';
import { getListServices, getListHrManagers, setDataJob, getJobInfo, getListUsersByRole, clearJobInfo } from 'actions';
import type { AppDispatch } from 'store';
import type { UserSimple } from 'store/reducers/types';
import { setRecruiterLeads } from 'store/reducers/commonReducer';
import { dataMapper, formDataMapper, userMapper, useServiceOptions, type JobFormValues } from './utils';
import './style.scss';

type SetDataJobBody = Parameters<typeof setDataJob>[0];

interface AddJobsProps {
  id?: string;
  isClone?: boolean;
}

const AddJobs = ({ id: propsId, isClone }: AddJobsProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<JobFormValues>();
  const dispatch = useDispatch<AppDispatch>();
  const { id: routeId } = useParams<{ id: string }>();
  const id = propsId ?? routeId;

  const [loading, setLoading] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [teamLeadOptions, setTeamLeadOptions] = useState<ReturnType<typeof userMapper>>([]);
  const [jobStatus, setJobStatus] = useState<string>(JOB_STATUSES.DRAFT);
  const [recruitersToReassign, setRecruitersToReassign] = useState<UserSimple[]>([]);

  const services = useSelector(getServices);
  const recruiters = useSelector(getHrManagers);
  const recruiterLeads = useSelector(getRecruiterLeads);
  const serviceOptions = useServiceOptions(services);

  const onCloseExitConfirmation = () => setOpenConfirmation(false);

  const onOkExitConfirmation = () => {
    if (propsId) {
      history.push(`/jobs/${propsId}/show/steps/`);
    } else {
      history.push('/jobs');
    }
  };

  const onCancel = () => {
    const { description, benefits, ...touchedValues } = form.getFieldsValue(null, meta => meta.touched);
    const description_initial = form.getFieldValue('description_initial');
    const benefits_initial = form.getFieldValue('benefits_initial');
    if (description !== description_initial || benefits !== benefits_initial || Object.keys(touchedValues).length) {
      setOpenConfirmation(true);
    } else {
      onOkExitConfirmation();
    }
  };

  const onOkSaveForm = (values: JobFormValues) => {
    const recruitersCurrentIds = form.getFieldValue('recruiters')?.map(r => r.value) || [];
    const recruitersInitial = form.getFieldValue('reassign_recruiters') || [];
    const recruitersToCheck = recruitersInitial.filter(
      r => r.is_active_responsible_recruiter && !recruitersCurrentIds.includes(r.uuid)
    );
    if (recruitersToCheck.length) {
      setRecruitersToReassign(recruitersToCheck);
    } else {
      setLoading(true);
      setDataJob({
        uuid: isClone ? undefined : id,
        ...formDataMapper(values),
      } as unknown as SetDataJobBody)
        .then(job => {
          setLoading(false);
          if (job?.uuid) {
            history.push(`/jobs/${job.uuid}/show/description`);
          }
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  const onCloseReassignPopup = () => setRecruitersToReassign([]);
  const onAfterReassignRecruiters = (r: Record<string, { value: string }>) => {
    const reassign_recruiters =
      r && Object.keys(r).length
        ? Object.entries(r).map(([key, v]) => ({ old_recruiter: key, new_recruiter: v.value }))
        : null;
    setDataJob({
      uuid: isClone ? undefined : id,
      ...formDataMapper({
        ...form.getFieldsValue(),
        reassign_recruiters: reassign_recruiters ?? undefined,
      }),
    } as unknown as SetDataJobBody).then(job => {
      if (job?.uuid) {
        history.push(`/jobs/${job.uuid}/show/description`);
      }
    });
    onCloseReassignPopup();
  };

  const getTeamLeads = useCallback(
    (service: string, resetTeamLead?: boolean) => {
      setTeamLeadOptions([]);
      getListUsersByRole({ role: [ROLES.TEAM_LEAD, ROLES.SERVICE_HEAD], service }).then(data => {
        const teamLeads = userMapper(data ?? []);
        const teamLead = form.getFieldValue('team_lead');
        setTeamLeadOptions(teamLeads);
        if (resetTeamLead && teamLead && !teamLeads.find(tl => tl.value === teamLead.value)) {
          form.setFieldValue('team_lead', undefined);
        }
      });
    },
    [form]
  );

  const onChangeValue = ({ service }: Partial<JobFormValues>) => {
    if (service) getTeamLeads(String(service.value), true);
  };

  useEffect(() => {
    dispatch(getListServices());
    dispatch(getListHrManagers({ role: ROLES.RECRUITER }));
    getListUsersByRole({ role: ROLES.RECRUITER_LEAD }).then(users => {
      dispatch(setRecruiterLeads((users ?? []).map(u => ({ value: u.uuid, label: u.full_name }))));
    });
    const mixpanel = new MixpanelService();
    if (propsId) {
      setLoading(true);
      dispatch(getJobInfo(propsId)).then(resp => {
        setLoading(false);
        if (resp?.service?.uuid) {
          form.setFields(dataMapper(resp, isClone));
          if (isClone && !resp.benefits) {
            form.setFieldsValue({ benefits: INITIAL_BENEFITS, benefits_initial: INITIAL_BENEFITS });
          }
          getTeamLeads(resp.service.uuid);
          setJobStatus(isClone ? JOB_STATUSES.DRAFT : resp.status);
        }
      });
      mixpanel.track('View Page.Edit Job', { 'Job ID': propsId });
    } else {
      form.setFieldsValue({ benefits: INITIAL_BENEFITS, benefits_initial: INITIAL_BENEFITS });
      dispatch(clearJobInfo());
      mixpanel.track('View Page.Create Job');
    }
    return () => {
      dispatch(clearJobInfo());
    };
  }, []);

  const getTitle = () => {
    if (propsId) {
      return isClone ? t('translation:add_jobs.title_clone_job') : t('translation:add_jobs.title_job_editing');
    }
    return t('translation:add_jobs.title_job_creation');
  };

  const getOkText = () => {
    if (propsId) {
      return isClone ? t('translation:add_jobs.button_save') : t('translation:add_jobs.button_save_changes');
    }
    return t('translation:add_jobs.button_create_job');
  };

  return (
    <>
      <Form form={form} onFinish={onOkSaveForm} onValuesChange={onChangeValue} validateTrigger="onSubmit">
        <div className="wrapper-content job-creation-page">
          <BreadCrumbs element={t('translation:add_jobs.breadcrumb_jobs')} elementName={getTitle()} path="/jobs" />
          <FormCreateEdit
            title={getTitle()}
            contentClassName="form-job-creation"
            onCancel={onCancel}
            okText={getOkText()}
            cancelText={t('translation:add_jobs.button_cancel')}
            loading={loading}
          >
            <JobCreationForm
              serviceOptions={serviceOptions}
              teamLeadOptions={teamLeadOptions}
              recruiters={recruiters}
              recruiterLeads={recruiterLeads as SelectOption[]}
              jobStatus={jobStatus}
            />
          </FormCreateEdit>
        </div>
      </Form>
      <Popup
        open={openConfirmation}
        title={POPUP_LABELS.DISCARD_CHANGES}
        cancelButtonProps={{ onClick: onCloseExitConfirmation, children: BUTTON_TEXT.BACK_TO_PAGE }}
        okButtonProps={{ onClick: onOkExitConfirmation, children: BUTTON_TEXT.DISCARD }}
      >
        {POPUP_BODY.CHANGES_NOT_SAVED}
      </Popup>
      <PopupReassignRecruiter
        open={!!recruitersToReassign.length}
        onClose={onCloseReassignPopup}
        onConfirm={onAfterReassignRecruiters}
        recruitersReassignFrom={recruitersToReassign}
      />
    </>
  );
};

export default AddJobs;
